import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { ItemArtefact } from '@mimir/common/dto/transferRequestDtos';
import { getExtname } from '@mimir/common/platformAbstraction/pathComposition';
import type { ArtifactReference } from '@mimir/primitive/artifactReference';
import type { NodeApi } from '@mimir/common/util/detectApi';
import type { KeldaPathMapping } from '../fileSync/premiereTypes';
import { convertUnixPathToWindowsLike } from './premiere';
import type { ItemWithSourceMedia } from './premiereItemTypes';
import type { Logger } from '@mimir/common/types/logger';
import { getItemIdRelativeSubfolder } from '@mimir/common/kelda/itemIdSubfolderUtils';
import { getFileName } from '../fileSync/defaultLocalStateOps';

/**
 * Don't invoke this function directly. Use instead a KeldPathOps object.
 */
export async function getLocalKeldaMappedPath(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  keldaPathMappings: KeldaPathMapping[],
  item: ItemWithSourceMedia,
  artefact: ItemArtefact,
  artifactReference: ArtifactReference,
  isFileAvailableLocallyInjected: (nodeApi: NodeApi, logger: Logger, localPath: string | null | undefined) => Promise<boolean>,
) {
  // Kelda mapped paths are only for externally ingested highres files
  if (artefact !== 'highres' && artefact !== 'proxy') return;

  const isWindows = services.applicationContext.isWindows;
  const pathSep = services.applicationContext.pathSep;

  const itemIdRelativeSubfolder = getItemIdRelativeSubfolder(item.id, '/');

  console.debug(`Evaluating kelda mapped paths for item ${item.id} with artefact ${artefact}, ingest source path ${item.ingest?.source.path}`);

  for (const pathMap of keldaPathMappings) {
    const candidatePathsToItem: string[] = [];
    const { keldaRelativePathToFolder, userDefinedPathToFolder } = pathMap;

    // Kelda relative path to the configured folder has to be stripped from the source path as the local path to that folder
    // is defined by the user
    if (pathMap.type === 'primary-root') {
      if (artefact === 'proxy') {
        let epFileExtension = '.mp4';
        if (artifactReference.type === 'TsSegmentRange') {
          epFileExtension = '.mxf';
        } else if (item.editProxyFormat === 'prores') {
          epFileExtension = '.mov';
        }

        candidatePathsToItem.push(`${item.id}-proxy${epFileExtension}`);
        candidatePathsToItem.push(`${itemIdRelativeSubfolder}/${item.id}-proxy${epFileExtension}`);
      } else {
        const fileName = getFileName(item);
        if (!fileName) {
          services.logger.warn(`originalFileName not found in item ${item.id}. Unable to look up Kelda mapped path`);
          return;
        }
        const highresExt = getExtname(fileName, '/');
        candidatePathsToItem.push(`${item.id}${highresExt}`);
        candidatePathsToItem.push(`${itemIdRelativeSubfolder}/${item.id}${highresExt}`);
      }
    } else if (artefact === 'highres') {
      let sourcePathMatchingKelda = '';
      if (item.ingest?.source.path.startsWith(keldaRelativePathToFolder)) {
        sourcePathMatchingKelda = item.ingest?.source.path;
      } else if (item.ingestSourceFullPath?.startsWith(keldaRelativePathToFolder)) {
        sourcePathMatchingKelda = item.ingestSourceFullPath;
      } else {
        continue;
      }

      const pathToItem = sourcePathMatchingKelda.substring(keldaRelativePathToFolder.length);
      candidatePathsToItem.push(pathToItem);
    }

    for (const pathToItem of candidatePathsToItem) {
      // As Kelda is run inside a docker container, the relative path to the item will always be unix-like
      const platformAdjustedPath = isWindows ? convertUnixPathToWindowsLike(pathToItem) : pathToItem;

      const delimiter = [userDefinedPathToFolder[userDefinedPathToFolder.length - 1], platformAdjustedPath[0]].includes(pathSep) ? '' : pathSep;
      const localPath = `${userDefinedPathToFolder}${delimiter}${platformAdjustedPath}`;

      console.debug(`Checking if file is available locally at Kelda mapped path: ${localPath}\n (userDefinedPathToFolder: ${userDefinedPathToFolder}, keldaRelativePathToFolder: ${keldaRelativePathToFolder}, platformAdjustedPath: ${platformAdjustedPath})`);
      if (await isFileAvailableLocallyInjected(services.applicationContext.nodeApi, services.logger, localPath)) {
        console.debug('File is available locally at Kelda mapped path:', localPath);
        return localPath;
      }
    }

  }
}
